/**
 * Dev-only mock user profiles for local development.
 *
 * Usage: set VITE_DEV_MOCK_USER in .env.local
 *
 *   VITE_DEV_MOCK_USER=agency_admin   → agency plan account owner
 *   VITE_DEV_MOCK_USER=solo_agent     → solo plan account owner (no agency admin)
 *   VITE_DEV_MOCK_USER=sub_agent      → agentUser team member
 *   VITE_DEV_MOCK_USER=parkpro_admin  → internal ParkPro staff
 *
 * All fields mirror the real /api/me response shape.
 * Never imported in production builds (import.meta.env.DEV guard in UserContext).
 */

const BASE = {
  userid:       'dev-user-001',
  email:        'dev@parkpro.local',
  username:     'dev_user',
  firstname:    'Dev',
  lastname:     'User',
  phone:        '555-0100',
  timezone:     'America/New_York',
  logourl:      null,
  status:       'active',
  publicslug:   'dev-user',
  agency_id:    'dev-agency-001',
  agencyName:   'Dev Agency',
};

const MOCK_USERS = {
  /** Agency-plan account owner — sees My Workspace + My Agency */
  agency_admin: {
    ...BASE,
    role:           'agentAdmin',
    AgencyPlan:     'agencyplus',
    AgencyMaxUsers: 10,
    planId:         'agencyplus',
    featureFlags: {
      branding:               true,
      api:                    true,
      impersonation:          false,
      extraUsers:             true,
      customQuestionnaireLink: true,
    },
  },

  /** Solo-plan account owner — sees My Workspace only */
  solo_agent: {
    ...BASE,
    userid:         'dev-user-002',
    email:          'solo@parkpro.local',
    username:       'dev_solo',
    firstname:      'Solo',
    role:           'agentAdmin',
    AgencyPlan:     'solo',
    AgencyMaxUsers: 1,
    planId:         'solo',
    featureFlags: {
      branding:               false,
      api:                    false,
      impersonation:          false,
      extraUsers:             false,
      customQuestionnaireLink: false,
    },
  },

  /** Team member (agentUser) — sees My Workspace only, no admin access */
  sub_agent: {
    ...BASE,
    userid:         'dev-user-003',
    email:          'sub@parkpro.local',
    username:       'dev_sub',
    firstname:      'Sub',
    role:           'agentUser',
    AgencyPlan:     'agencyplus',
    AgencyMaxUsers: 10,
    planId:         'agencyplus',
    featureFlags: {
      branding:               true,
      api:                    false,
      impersonation:          false,
      extraUsers:             false,
      customQuestionnaireLink: false,
    },
  },

  /** ParkPro internal staff — bypasses all phase and plan gates */
  parkpro_admin: {
    ...BASE,
    userid:         'dev-user-004',
    email:          'admin@parkpro.local',
    username:       'dev_admin',
    firstname:      'ParkPro',
    lastname:       'Admin',
    role:           'parkProAdmin',
    AgencyPlan:     'enterprise',
    AgencyMaxUsers: -1,
    planId:         'enterprise',
    featureFlags: {
      branding:               true,
      api:                    true,
      impersonation:          true,
      extraUsers:             true,
      customQuestionnaireLink: true,
    },
  },
};

/** Mock usage summary matching the /api/me/usage response shape */
export const MOCK_USAGE = {
  monthly_itineraries: 3,
  monthly_limit:       12,
  usage_percentage:    25,
  can_create:          true,
  plan_name:           'agency+',
};

/**
 * Get the mock user for the current VITE_DEV_MOCK_USER value.
 * Returns null if the key is unrecognised (falls back to normal auth).
 *
 * @returns {object|null}
 */
export const getMockUser = () => {
  const key = import.meta.env.VITE_DEV_MOCK_USER;
  return MOCK_USERS[key] ?? null;
};
